// DOM menus: title screen (world list + create), pause menu, and settings.
// These are plain overlays appended to <body>; the game shows/hides them.

import { config, DEFAULT_CONFIG } from './sim/config';
import { clickStack, HOTBAR_SIZE, Inventory, INVENTORY_SIZE, Slot } from './sim/inventory';
import { itemName, maxStackOf } from './sim/items';
import { matchRecipe } from './sim/crafting';
import { saveSettings } from './settings';
import { WorldMeta } from './persist';
import { itemSwatch } from './render/swatch';

const PANEL =
  'background:rgba(18,22,30,.92);border:1px solid rgba(255,255,255,.12);border-radius:10px;' +
  'padding:24px 28px;color:#eaeef5;font-family:system-ui,sans-serif;min-width:340px;';
const BTN =
  'font-size:15px;padding:9px 18px;cursor:pointer;border:none;border-radius:6px;background:#5aa64b;color:#fff;';
const BTN_SECONDARY =
  'font-size:15px;padding:9px 18px;cursor:pointer;border:1px solid rgba(255,255,255,.25);border-radius:6px;background:transparent;color:#eaeef5;';

function overlay(): HTMLDivElement {
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;' +
    'background:linear-gradient(160deg,#0d1b2a,#1b2a3a);z-index:10;';
  document.body.appendChild(el);
  return el;
}

export interface TitleHandlers {
  onPlayExisting: (name: string) => void;
  onCreate: (name: string, seedText: string) => void;
  onDelete: (name: string) => void;
  onSettings: () => void;
}

export class TitleScreen {
  private root: HTMLDivElement;
  private listEl: HTMLDivElement;

  constructor(private handlers: TitleHandlers) {
    this.root = overlay();
    const panel = document.createElement('div');
    panel.style.cssText = PANEL + 'width:420px;';

    const title = document.createElement('div');
    title.textContent = 'BLOCKFALL';
    title.style.cssText = 'font-size:40px;font-weight:800;letter-spacing:3px;margin-bottom:2px;';
    const sub = document.createElement('div');
    sub.textContent = 'Gather · Fight · Build';
    sub.style.cssText = 'opacity:.7;margin-bottom:18px;';

    const listTitle = document.createElement('div');
    listTitle.textContent = 'Your worlds';
    listTitle.style.cssText = 'font-weight:600;margin-bottom:6px;';
    this.listEl = document.createElement('div');
    this.listEl.style.cssText =
      'display:flex;flex-direction:column;gap:6px;margin-bottom:18px;max-height:220px;overflow:auto;';

    // New-world form.
    const form = document.createElement('div');
    form.style.cssText = 'display:flex;flex-direction:column;gap:8px;margin-bottom:16px;';
    const nameInput = document.createElement('input');
    nameInput.placeholder = 'New world name';
    nameInput.value = 'My World';
    const seedInput = document.createElement('input');
    seedInput.placeholder = 'Seed (optional)';
    for (const inp of [nameInput, seedInput]) {
      inp.style.cssText =
        'padding:8px 10px;border-radius:6px;border:1px solid rgba(255,255,255,.2);background:#0f141c;color:#eaeef5;';
    }
    const createBtn = document.createElement('button');
    createBtn.textContent = 'Create & Play';
    createBtn.style.cssText = BTN;
    createBtn.addEventListener('click', () => {
      const name = nameInput.value.trim() || 'World';
      this.handlers.onCreate(name, seedInput.value.trim());
    });
    form.append(nameInput, seedInput, createBtn);

    const settingsBtn = document.createElement('button');
    settingsBtn.textContent = 'Settings';
    settingsBtn.style.cssText = BTN_SECONDARY;
    settingsBtn.addEventListener('click', () => this.handlers.onSettings());

    panel.append(title, sub, listTitle, this.listEl, form, settingsBtn);
    this.root.appendChild(panel);
  }

  refresh(worlds: WorldMeta[]): void {
    this.listEl.innerHTML = '';
    if (worlds.length === 0) {
      const empty = document.createElement('div');
      empty.textContent = 'No saved worlds yet — create one below.';
      empty.style.cssText = 'opacity:.6;font-size:14px;';
      this.listEl.appendChild(empty);
      return;
    }
    for (const w of worlds) {
      const row = document.createElement('div');
      row.style.cssText =
        'display:flex;align-items:center;gap:8px;background:rgba(255,255,255,.05);border-radius:6px;padding:8px 10px;';
      const label = document.createElement('div');
      label.style.cssText = 'flex:1;';
      const when = new Date(w.savedAt).toLocaleString();
      label.innerHTML = `<b>${escapeHtml(w.name)}</b><br><span style="opacity:.6;font-size:12px">seed ${w.seed} · ${when}</span>`;
      const play = document.createElement('button');
      play.textContent = 'Play';
      play.style.cssText = BTN + 'padding:6px 14px;';
      play.addEventListener('click', () => this.handlers.onPlayExisting(w.name));
      const del = document.createElement('button');
      del.textContent = '✕';
      del.title = 'Delete world';
      del.style.cssText = BTN_SECONDARY + 'padding:6px 10px;';
      del.addEventListener('click', () => this.handlers.onDelete(w.name));
      row.append(label, play, del);
      this.listEl.appendChild(row);
    }
  }

  show(): void {
    this.root.style.display = 'flex';
  }
  hide(): void {
    this.root.style.display = 'none';
  }
  dispose(): void {
    this.root.remove();
  }
}

export interface PauseHandlers {
  onResume: () => void;
  onSettings: () => void;
  onQuit: () => void;
}

export class PauseMenu {
  private root: HTMLDivElement;

  constructor(handlers: PauseHandlers) {
    this.root = overlay();
    this.root.style.background = 'rgba(6,10,16,.7)';
    const panel = document.createElement('div');
    panel.style.cssText = PANEL + 'display:flex;flex-direction:column;gap:10px;text-align:center;';
    const title = document.createElement('div');
    title.textContent = 'Paused';
    title.style.cssText = 'font-size:26px;font-weight:700;margin-bottom:8px;';
    const resume = mkButton('Resume', BTN, handlers.onResume);
    const settings = mkButton('Settings', BTN_SECONDARY, handlers.onSettings);
    const quit = mkButton('Save & Quit to Title', BTN_SECONDARY, handlers.onQuit);
    panel.append(title, resume, settings, quit);
    this.root.appendChild(panel);
  }

  show(): void {
    this.root.style.display = 'flex';
  }
  hide(): void {
    this.root.style.display = 'none';
  }
  dispose(): void {
    this.root.remove();
  }
}

export class SettingsPanel {
  private root: HTMLDivElement;
  private onChange: () => void;
  private onClose: () => void;

  constructor(onChange: () => void, onClose: () => void) {
    this.onChange = onChange;
    this.onClose = onClose;
    this.root = overlay();
    this.root.style.background = 'rgba(6,10,16,.8)';
    const panel = document.createElement('div');
    panel.style.cssText = PANEL + 'width:400px;';
    const title = document.createElement('div');
    title.textContent = 'Settings';
    title.style.cssText = 'font-size:24px;font-weight:700;margin-bottom:16px;';
    panel.appendChild(title);

    panel.appendChild(
      this.slider(
        'Render distance (chunks)',
        2,
        16,
        1,
        () => config.viewDistanceChunks,
        (v) => (config.viewDistanceChunks = v),
      ),
    );
    panel.appendChild(
      this.slider(
        'Mouse sensitivity',
        0.0005,
        0.006,
        0.0001,
        () => config.mouseSensitivity,
        (v) => (config.mouseSensitivity = v),
        4,
      ),
    );
    panel.appendChild(
      this.slider(
        'Field of view',
        60,
        110,
        1,
        () => config.fov,
        (v) => (config.fov = v),
      ),
    );
    panel.appendChild(
      this.slider(
        'Master volume',
        0,
        1,
        0.05,
        () => config.masterVolume,
        (v) => (config.masterVolume = v),
        2,
      ),
    );
    panel.appendChild(
      this.slider(
        'Zombie attack damage',
        0,
        10,
        1,
        () => config.zombieAttackDamage,
        (v) => (config.zombieAttackDamage = v),
      ),
    );

    const buttons = document.createElement('div');
    buttons.style.cssText = 'display:flex;gap:10px;margin-top:18px;';
    buttons.appendChild(mkButton('Reset to defaults', BTN_SECONDARY, () => this.reset(panel)));
    buttons.appendChild(mkButton('Done', BTN, () => this.onClose()));
    panel.appendChild(buttons);
    this.root.appendChild(panel);
  }

  private slider(
    label: string,
    min: number,
    max: number,
    step: number,
    get: () => number,
    set: (v: number) => void,
    decimals = 0,
  ): HTMLDivElement {
    const row = document.createElement('div');
    row.style.cssText = 'margin-bottom:12px;';
    const head = document.createElement('div');
    head.style.cssText =
      'display:flex;justify-content:space-between;font-size:14px;margin-bottom:4px;';
    const name = document.createElement('span');
    name.textContent = label;
    const value = document.createElement('span');
    value.textContent = get().toFixed(decimals);
    value.style.opacity = '.8';
    head.append(name, value);
    const input = document.createElement('input');
    input.type = 'range';
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    input.value = String(get());
    input.style.cssText = 'width:100%;';
    input.dataset.setter = label;
    input.addEventListener('input', () => {
      const v = Number(input.value);
      set(v);
      value.textContent = v.toFixed(decimals);
      saveSettings();
      this.onChange();
    });
    row.append(head, input);
    return row;
  }

  private reset(panel: HTMLElement): void {
    Object.assign(config, DEFAULT_CONFIG);
    saveSettings();
    this.syncInputs(panel);
    this.onChange();
  }

  private syncInputs(panel: HTMLElement): void {
    const getters: Record<string, () => number> = {
      'Render distance (chunks)': () => config.viewDistanceChunks,
      'Mouse sensitivity': () => config.mouseSensitivity,
      'Field of view': () => config.fov,
      'Master volume': () => config.masterVolume,
      'Zombie attack damage': () => config.zombieAttackDamage,
    };
    const decimals: Record<string, number> = {
      'Mouse sensitivity': 4,
      'Master volume': 2,
    };
    panel.querySelectorAll('input[type=range]').forEach((el) => {
      const input = el as HTMLInputElement;
      const key = input.dataset.setter ?? '';
      const g = getters[key];
      if (!g) return;
      input.value = String(g());
      // Update the value label sitting in the row header next to this slider.
      const header = input.parentElement?.firstElementChild;
      const valueSpan = header?.lastElementChild as HTMLElement | undefined;
      if (valueSpan) valueSpan.textContent = g().toFixed(decimals[key] ?? 0);
    });
  }

  show(): void {
    this.root.style.display = 'flex';
  }
  hide(): void {
    this.root.style.display = 'none';
  }
  dispose(): void {
    this.root.remove();
  }
}

export class InventoryScreen {
  private root: HTMLDivElement;
  private grid: HTMLDivElement;
  private hotbar: HTMLDivElement;
  private heldEl: HTMLDivElement;
  private inv: Inventory;
  private held: Slot | null = null;

  constructor(inv: Inventory, onClose: () => void) {
    this.inv = inv;
    this.root = overlay();
    this.root.style.background = 'rgba(6,10,16,.72)';
    const panel = document.createElement('div');
    panel.style.cssText = PANEL + 'display:flex;flex-direction:column;gap:14px;';

    const title = document.createElement('div');
    title.textContent = 'Inventory';
    title.style.cssText = 'font-size:22px;font-weight:700;';

    this.grid = document.createElement('div');
    this.grid.style.cssText = 'display:grid;grid-template-columns:repeat(9,44px);gap:4px;';
    const spacer = document.createElement('div');
    spacer.style.height = '6px';
    this.hotbar = document.createElement('div');
    this.hotbar.style.cssText = 'display:grid;grid-template-columns:repeat(9,44px);gap:4px;';

    const hint = document.createElement('div');
    hint.textContent = 'Click to pick up / place a stack · E or Esc to close';
    hint.style.cssText = 'font-size:12px;opacity:.65;';

    panel.append(title, this.grid, spacer, this.hotbar, hint);
    this.root.appendChild(panel);

    // Floating held-stack cursor.
    this.heldEl = document.createElement('div');
    this.heldEl.style.cssText =
      'position:fixed;width:30px;height:30px;pointer-events:none;display:none;transform:translate(-50%,-50%);z-index:20;';
    this.root.appendChild(this.heldEl);

    this.root.addEventListener('mousemove', (e) => {
      this.heldEl.style.left = `${e.clientX}px`;
      this.heldEl.style.top = `${e.clientY}px`;
    });
    // Esc closes while open (the E key is handled centrally by Input).
    window.addEventListener('keydown', (e) => {
      if (this.root.style.display !== 'flex') return;
      if (e.code === 'Escape') onClose();
    });
  }

  private cell(index: number): HTMLDivElement {
    const slot = this.inv.slots[index];
    const cell = document.createElement('div');
    cell.style.cssText =
      'width:44px;height:44px;box-sizing:border-box;border:2px solid rgba(255,255,255,.3);' +
      'background:rgba(0,0,0,.35);position:relative;display:flex;align-items:center;justify-content:center;cursor:pointer;';
    if (slot) {
      const sw = document.createElement('div');
      sw.style.cssText = `width:28px;height:28px;background:${itemSwatch(slot.item)};border:1px solid rgba(0,0,0,.4);`;
      sw.title = itemName(slot.item);
      cell.appendChild(sw);
      if (slot.count > 1) {
        const cnt = document.createElement('div');
        cnt.textContent = String(slot.count);
        cnt.style.cssText =
          'position:absolute;right:2px;bottom:0;font-size:12px;text-shadow:0 1px 2px #000;color:#fff;';
        cell.appendChild(cnt);
      }
    }
    cell.addEventListener('click', () => {
      this.held = this.inv.clickSlot(index, this.held);
      this.refresh();
    });
    return cell;
  }

  refresh(): void {
    this.grid.innerHTML = '';
    for (let i = HOTBAR_SIZE; i < INVENTORY_SIZE; i++) this.grid.appendChild(this.cell(i));
    this.hotbar.innerHTML = '';
    for (let i = 0; i < HOTBAR_SIZE; i++) this.hotbar.appendChild(this.cell(i));

    if (this.held) {
      this.heldEl.style.display = 'block';
      this.heldEl.style.background = itemSwatch(this.held.item);
      this.heldEl.style.border = '1px solid rgba(0,0,0,.5)';
      this.heldEl.title = `${itemName(this.held.item)} ×${this.held.count}`;
    } else {
      this.heldEl.style.display = 'none';
    }
  }

  show(): void {
    this.refresh();
    this.root.style.display = 'flex';
  }

  /** Hide, returning any held stack to the inventory so it isn't lost. */
  hide(): void {
    this.inv.returnHeld(this.held);
    this.held = null;
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}

export class CraftingScreen {
  private root: HTMLDivElement;
  private gridEl: HTMLDivElement;
  private resultEl: HTMLDivElement;
  private storeEl: HTMLDivElement;
  private hotbarEl: HTMLDivElement;
  private heldEl: HTMLDivElement;
  private inv: Inventory;
  private grid: (Slot | null)[] = new Array(9).fill(null);
  private held: Slot | null = null;

  constructor(inv: Inventory, onClose: () => void) {
    this.inv = inv;
    this.root = overlay();
    this.root.style.background = 'rgba(6,10,16,.72)';
    const panel = document.createElement('div');
    panel.style.cssText = PANEL + 'display:flex;flex-direction:column;gap:14px;';

    const title = document.createElement('div');
    title.textContent = 'Crafting';
    title.style.cssText = 'font-size:22px;font-weight:700;';

    // Crafting row: 3×3 grid → result.
    const craftRow = document.createElement('div');
    craftRow.style.cssText = 'display:flex;align-items:center;gap:16px;';
    this.gridEl = document.createElement('div');
    this.gridEl.style.cssText = 'display:grid;grid-template-columns:repeat(3,44px);gap:4px;';
    const arrow = document.createElement('div');
    arrow.textContent = '→';
    arrow.style.cssText = 'font-size:28px;opacity:.7;';
    this.resultEl = document.createElement('div');
    craftRow.append(this.gridEl, arrow, this.resultEl);

    const invLabel = document.createElement('div');
    invLabel.textContent = 'Inventory';
    invLabel.style.cssText = 'font-weight:600;opacity:.8;';
    this.storeEl = document.createElement('div');
    this.storeEl.style.cssText = 'display:grid;grid-template-columns:repeat(9,44px);gap:4px;';
    const spacer = document.createElement('div');
    spacer.style.height = '6px';
    this.hotbarEl = document.createElement('div');
    this.hotbarEl.style.cssText = 'display:grid;grid-template-columns:repeat(9,44px);gap:4px;';

    const hint = document.createElement('div');
    hint.textContent = 'Click to move stacks · click the result to craft · Esc closes';
    hint.style.cssText = 'font-size:12px;opacity:.65;';

    panel.append(title, craftRow, invLabel, this.storeEl, spacer, this.hotbarEl, hint);
    this.root.appendChild(panel);

    this.heldEl = document.createElement('div');
    this.heldEl.style.cssText =
      'position:fixed;width:30px;height:30px;pointer-events:none;display:none;transform:translate(-50%,-50%);z-index:20;';
    this.root.appendChild(this.heldEl);
    this.root.addEventListener('mousemove', (e) => {
      this.heldEl.style.left = `${e.clientX}px`;
      this.heldEl.style.top = `${e.clientY}px`;
    });
    window.addEventListener('keydown', (e) => {
      if (this.root.style.display === 'flex' && e.code === 'Escape') onClose();
    });
  }

  private cell(slot: Slot | null, onClick: () => void): HTMLDivElement {
    const cell = document.createElement('div');
    cell.style.cssText =
      'width:44px;height:44px;box-sizing:border-box;border:2px solid rgba(255,255,255,.3);' +
      'background:rgba(0,0,0,.35);position:relative;display:flex;align-items:center;justify-content:center;cursor:pointer;';
    if (slot) {
      const sw = document.createElement('div');
      sw.style.cssText = `width:28px;height:28px;background:${itemSwatch(slot.item)};border:1px solid rgba(0,0,0,.4);`;
      sw.title = itemName(slot.item);
      cell.appendChild(sw);
      if (slot.count > 1) {
        const cnt = document.createElement('div');
        cnt.textContent = String(slot.count);
        cnt.style.cssText =
          'position:absolute;right:2px;bottom:0;font-size:12px;text-shadow:0 1px 2px #000;color:#fff;';
        cell.appendChild(cnt);
      }
    }
    cell.addEventListener('click', onClick);
    return cell;
  }

  private craftResult(): Slot | null {
    const out = matchRecipe(this.grid.map((s) => (s ? s.item : null)));
    return out ? { item: out.item, count: out.count } : null;
  }

  private takeResult(): void {
    const result = this.craftResult();
    if (!result) return;
    if (
      this.held &&
      (this.held.item !== result.item || this.held.count + result.count > maxStackOf(result.item))
    ) {
      return; // can't hold the output
    }
    for (let i = 0; i < 9; i++) {
      const s = this.grid[i];
      if (s) {
        s.count -= 1;
        if (s.count <= 0) this.grid[i] = null;
      }
    }
    if (this.held) this.held.count += result.count;
    else this.held = { item: result.item, count: result.count };
    this.refresh();
  }

  refresh(): void {
    this.gridEl.innerHTML = '';
    for (let i = 0; i < 9; i++) {
      this.gridEl.appendChild(
        this.cell(this.grid[i], () => {
          this.held = clickStack(this.grid, i, this.held);
          this.refresh();
        }),
      );
    }
    this.resultEl.innerHTML = '';
    this.resultEl.appendChild(this.cell(this.craftResult(), () => this.takeResult()));

    this.storeEl.innerHTML = '';
    for (let i = HOTBAR_SIZE; i < INVENTORY_SIZE; i++) {
      this.storeEl.appendChild(
        this.cell(this.inv.slots[i], () => {
          this.held = this.inv.clickSlot(i, this.held);
          this.refresh();
        }),
      );
    }
    this.hotbarEl.innerHTML = '';
    for (let i = 0; i < HOTBAR_SIZE; i++) {
      this.hotbarEl.appendChild(
        this.cell(this.inv.slots[i], () => {
          this.held = this.inv.clickSlot(i, this.held);
          this.refresh();
        }),
      );
    }

    if (this.held) {
      this.heldEl.style.display = 'block';
      this.heldEl.style.background = itemSwatch(this.held.item);
      this.heldEl.style.border = '1px solid rgba(0,0,0,.5)';
      this.heldEl.title = `${itemName(this.held.item)} ×${this.held.count}`;
    } else {
      this.heldEl.style.display = 'none';
    }
  }

  show(): void {
    this.refresh();
    this.root.style.display = 'flex';
  }

  /** Return grid contents + held stack to the inventory so nothing is lost. */
  hide(): void {
    for (let i = 0; i < 9; i++) {
      const s = this.grid[i];
      if (s) this.inv.add(s.item, s.count);
      this.grid[i] = null;
    }
    this.inv.returnHeld(this.held);
    this.held = null;
    this.root.style.display = 'none';
  }

  dispose(): void {
    this.root.remove();
  }
}

function mkButton(text: string, style: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.textContent = text;
  b.style.cssText = style;
  b.addEventListener('click', onClick);
  return b;
}

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!,
  );
}
